import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  type AssetListResponse,
  type PackingWriteResponse,
  type PublicAsset,
  type PublicTote,
  type ShipmentResponse,
  type ToteListResponse,
} from '@inventohub/shared';
import { HelpLink } from '../components/HelpLink';
import { PackingEditor } from '../components/PackingEditor';
import { ApiClientError, apiRequest } from '../lib/api';

export function ShipmentDetailPage(): JSX.Element {
  const { id } = useParams();
  const [detail, setDetail] = useState<ShipmentResponse | null>(null);
  const [totes, setTotes] = useState<PublicTote[]>([]);
  const [assets, setAssets] = useState<PublicAsset[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const [shipmentBody, toteBody, assetBody] = await Promise.all([
          apiRequest<ShipmentResponse>(`/api/shipments/${id}`),
          apiRequest<ToteListResponse>('/api/totes'),
          apiRequest<AssetListResponse>('/api/assets'),
        ]);
        if (cancelled) return;
        setDetail(shipmentBody);
        setTotes(toteBody.totes);
        setAssets(assetBody.assets);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError ? caught.message : 'Could not load this shipment.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  function onPacked(body: PackingWriteResponse): void {
    setDetail((current) =>
      current
        ? { ...current, packedTotes: body.packedTotes, packedAssets: body.packedAssets }
        : current,
    );
  }

  if (!detail && !error) {
    return (
      <section className="card">
        <p>Loading shipment…</p>
      </section>
    );
  }

  return (
    <section data-testid="shipment-detail-page">
      <div className="card">
        <p className="meta">
          <Link to="/trailers">Trailers and shipments</Link>
        </p>
        <div className="card__title-row">
          <h2>{detail?.shipment.name ?? 'Shipment'}</h2>
          <HelpLink slug="shipped-via" />
        </div>
        <p className="meta">
          Named EWP grouping for a trip that is not an EWP trailer. Not a third-party truck row.
        </p>
        {error ? <p className="status status--error">{error}</p> : null}
        {detail?.shipment.notes ? <p>{detail.shipment.notes}</p> : null}
      </div>
      {detail ? (
        <PackingEditor
          packingPath={`/api/shipments/${detail.shipment.id}/packing`}
          packedTotes={detail.packedTotes}
          packedAssets={detail.packedAssets}
          totes={totes}
          assets={assets}
          onPacked={onPacked}
        />
      ) : null}
    </section>
  );
}
